import React, { useEffect, useRef } from "react";

import { useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";

import { persistor } from "../store";
import { setAuthenticated, setUser, setTokens } from "../store/slices/authSlice";
import { clearServiceData } from "../store/slices/serviceSlice";

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const handleUserLoaded = async () => {
      if (auth.isAuthenticated && auth.user) {
        const currentUserId = auth.user?.profile?.sub || "unknown";

        // Only clear data if a DIFFERENT user logs in (not when same user returns)
        if (previousUserIdRef.current !== null && previousUserIdRef.current !== currentUserId) {
          await persistor.purge();
          dispatch(clearServiceData());
        }
        previousUserIdRef.current = currentUserId;

        dispatch(setAuthenticated(true));
        dispatch(
          setUser({
            id: currentUserId,
            name: auth.user?.profile?.name || "Unknown User",
            email: auth.user?.profile?.email || "unknown@example.com",
            givenName: (auth.user?.profile as any)?.given_name || "",
            familyName: (auth.user?.profile as any)?.family_name || "",
          }),
        );

        // Try different ways to access the token from OIDC
        const accessToken = auth.user.access_token || auth.user.id_token || (auth as any).accessToken;
        const refreshToken = auth.user.refresh_token || (auth as any).refreshToken;

        if (accessToken) {
          dispatch(
            setTokens({
              token: accessToken,
              refreshToken: refreshToken || "",
            }),
          );
        }
      }
    };

    handleUserLoaded();
  }, [auth.isAuthenticated, auth.user, dispatch]);

  return <>{children}</>;
};
